import ServerBackups from "@/components/ServerBackups";
import { useEffect, useState } from "react";
import {
  Archive, RotateCcw, ShieldCheck, GitCompare, Camera, DatabaseBackup, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Loading, EmptyState, fmtBytes } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

const TYPE_COLORS = {
  created: "text-sky-400", deploy: "text-violet-400", manual: "text-emerald-400",
  "pre-deploy": "text-amber-400", "pre-restore": "text-amber-400", "file-edit": "text-zinc-400",
};

export default function Backups() {
  const [sites, setSites] = useState([]);
  const [slug, setSlug] = useState("");
  const [versions, setVersions] = useState(null);
  const [diff, setDiff] = useState(null);
  const [busy, setBusy] = useState(false);
  const { canWrite } = useAuth();

  useEffect(() => { api.get("/sites").then((s) => { setSites(s); if (s.length) setSlug(s[0].slug); }); }, []);
  const load = () => slug && api.get(`/versions/${slug}`).then(setVersions).catch(() => setVersions([]));
  useEffect(() => { setVersions(null); load(); }, [slug]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const showDiff = async (v) => {
    try { const d = await api.get(`/versions/${slug}/${v.version_id}/diff`); setDiff({ ...d, version: v }); }
    catch { toast.error("Diff failed"); }
  };

  return (
    <div data-testid="backups-page">
      <PageHeader
        title="Backups & Versions"
        breadcrumb="cannon / backups"
        subtitle="Auto-snapshot before every change · retention 20 (keep known-good)"
        actions={
          <div className="flex items-center gap-2">
            <Select value={slug} onValueChange={setSlug}>
              <SelectTrigger className="w-52 bg-zinc-900 border-zinc-800" data-testid="backup-site-select"><SelectValue placeholder="Select site" />
      <ServerBackups /></SelectTrigger>
              <SelectContent className="bg-[#0c0c0e] border-zinc-800">
                {sites.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {canWrite && <>
              <button onClick={() => run(() => api.post(`/versions/${slug}/backup`), "Snapshot created")} disabled={busy || !slug}
                data-testid="manual-backup-btn" className="inline-flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-800 disabled:opacity-50">
                <Camera className="h-4 w-4" /> Backup
              </button>
              <button onClick={() => run(() => api.post(`/backup-all`), "Backed up all sites")} disabled={busy}
                data-testid="backup-all-btn" className="inline-flex items-center gap-2 rounded-md bg-zinc-100 text-zinc-900 px-3 py-1.5 text-sm font-medium hover:bg-white disabled:opacity-50">
                <DatabaseBackup className="h-4 w-4" /> Back Up All
              </button>
            </>}
          </div>
        }
      />

      {canWrite && slug && (
        <div className="mb-4">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="rollback-btn" className="inline-flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 text-amber-300 px-3 py-1.5 text-sm hover:bg-amber-500/10">
                <RotateCcw className="h-4 w-4" /> One-click Rollback to Known-Good
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#0c0c0e] border-zinc-800">
              <AlertDialogHeader><AlertDialogTitle>Rollback {slug}?</AlertDialogTitle>
                <AlertDialogDescription>Restores the version marked known-good. Current state is snapshotted first.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => run(() => api.post(`/versions/${slug}/rollback`), "Rolled back")} className="bg-amber-600 hover:bg-amber-500">Rollback</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {!versions ? <Loading /> : versions.length === 0 ? (
        <EmptyState icon={Archive} title="No versions yet" />
      ) : (
        <div className="border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-900/50 text-left text-xs font-semibold uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-2.5">Version</th><th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Note</th><th className="px-4 py-2.5">Files</th>
                <th className="px-4 py-2.5">Size</th><th className="px-4 py-2.5">When</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.version_id} className="border-b border-zinc-800/70 hover:bg-zinc-900/40" data-testid={`version-row-${v.version_id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-300">{v.version_id}{v.known_good && <ShieldCheck className="inline h-3.5 w-3.5 text-emerald-400 ml-1" />}</td>
                  <td className={cn("px-4 py-3 font-mono text-xs", TYPE_COLORS[v.type] || "text-zinc-400")}>{v.type}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs max-w-[200px] truncate">{v.note}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{v.file_count}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{fmtBytes(v.size_bytes)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{new Date(v.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => showDiff(v)} title="Diff vs current" className="p-1 text-zinc-500 hover:text-sky-400"><GitCompare className="h-4 w-4" /></button>
                    {canWrite && <>
                      <button onClick={() => run(() => api.post(`/versions/${slug}/${v.version_id}/known-good`), "Marked known-good")} title="Mark known-good" className="p-1 text-zinc-500 hover:text-emerald-400"><ShieldCheck className="h-4 w-4" /></button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><button title="Restore" data-testid={`restore-${v.version_id}`} className="p-1 text-zinc-500 hover:text-amber-400"><RotateCcw className="h-4 w-4" /></button></AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#0c0c0e] border-zinc-800">
                          <AlertDialogHeader><AlertDialogTitle>Restore {v.version_id}?</AlertDialogTitle>
                            <AlertDialogDescription>Current state snapshotted first. Node app will be stopped.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => run(() => api.post(`/versions/${slug}/${v.version_id}/restore`), "Restored")} className="bg-amber-600 hover:bg-amber-500">Restore</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!diff} onOpenChange={(o) => !o && setDiff(null)}>
        <DialogContent className="bg-[#0c0c0e] border-zinc-800 max-w-2xl" data-testid="diff-dialog">
          <DialogHeader><DialogTitle className="font-head">Diff · {diff?.version?.version_id} vs current</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3 text-xs font-mono max-h-[60vh] overflow-auto">
            <DiffCol title="ADDED" color="text-emerald-400" items={diff?.added} />
            <DiffCol title="REMOVED" color="text-rose-400" items={diff?.removed} />
            <DiffCol title="CHANGED" color="text-amber-400" items={diff?.changed} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DiffCol({ title, color, items = [] }) {
  return (
    <div>
      <div className={cn("uppercase tracking-widest mb-2", color)}>{title} ({items.length})</div>
      <div className="space-y-1">{items.map((i) => <div key={i} className="text-zinc-400 truncate">{i}</div>)}
        {items.length === 0 && <div className="text-zinc-700">—</div>}</div>
    </div>
  );
}
